import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { sortRegions } from '../utils/sort';
import PageLayout from '../components/PageLayout';
import { useLocation } from 'react-router-dom';
import {
  fetchMapMarkerCdnOptions,
  flattenMapMarkerOptions,
  type MapMarkerCdnGrouped,
} from '../utils/mapMarkerCdn';
import {
  BrandingSelectPreview,
  VenueBrandingPair,
  VenueBrandingThumb,
  VenueNameWithBranding,
} from '../components/VenueBrandingPreview';
import './VenueGroupingPage.css';

interface Address {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  state_code?: string;
  postal_code?: string;
  country?: string;
}

interface VenueGroup {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  address?: Address;
  map_marker?: string;
  venue_icon?: string;
  website?: string;
  phone?: string;
  region_name: string;
  created_at: string;
  last_updated: string;
}

interface VenueSimple {
  id: string;
  name: string;
}

interface VenueWithDetails {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  address?: string;
}

interface VenueGroupingWithVenues {
  venueGroup: VenueGroup;
  venueList: VenueSimple[];
}

interface VenueGroupingDetail {
  venueGroup: VenueGroup;
  venueList: VenueWithDetails[];
}

const VenueGroupingPage: React.FC = () => {
  const { token } = useAuth();
  const location = useLocation();
  const [selectedRegion, setSelectedRegion] = useState<string>('chicago');
  const [venueGroups, setVenueGroups] = useState<VenueGroupingWithVenues[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<VenueGroup | null>(null);
  const [formData, setFormData] = useState<Partial<VenueGroup & { venue_ids: string[] }>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [regions, setRegions] = useState<Array<{ region_name: string; metro_name: string }>>([]);
  const [mapMarkerGroups, setMapMarkerGroups] = useState<MapMarkerCdnGrouped>({
    shared: [],
    regional: [],
  });
  const [loadingMapMarkers, setLoadingMapMarkers] = useState(false);
  const [availableVenues, setAvailableVenues] = useState<Array<{ id: string; name: string }>>([]);
  const [venueSearchTerm, setVenueSearchTerm] = useState('');
  const [selectedVenueIds, setSelectedVenueIds] = useState<string[]>([]);

  useEffect(() => {
    if (token && selectedRegion) {
      loadVenueGroups();
      loadMapMarkers();
      loadRegions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedRegion]);

  // Handle pre-filled venue IDs from navigation (when coming from VenuesPage)
  // or viewing a specific venue group
  useEffect(() => {
    if (!token) return;
    const searchParams = new URLSearchParams(location.search);
    const venueIds = searchParams.get('venue_ids');
    const venueGroupId = searchParams.get('venue_group_id');
    
    if (venueIds) {
      const ids = venueIds.split(',').filter(id => id.trim() !== '');
      setSelectedVenueIds(ids);
      setShowForm(true);
      setFormData(prev => ({
        ...prev,
        venue_ids: ids,
      }));
      // Load venue names for the selected IDs
      loadVenueNamesForIds(ids);
    } else if (venueGroupId) {
      // View specific venue group
      loadVenueGroupDetails(venueGroupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, token, selectedRegion]);

  const loadVenueGroupDetails = async (groupId: string) => {
    if (!token) return;
    try {
      const response = await apiRequest<VenueGroupingDetail>(
        `/admin/venues/groups/${groupId}`,
        token
      );
      setEditingGroup(response.venueGroup);
      setFormData({
        ...response.venueGroup,
        venue_ids: response.venueList.map(v => v.id),
      });
      setSelectedVenueIds(response.venueList.map(v => v.id));
      
      // Load venue names for display
      const venueMap = new Map<string, string>();
      response.venueList.forEach(v => {
        venueMap.set(v.id, v.name);
      });
      setAvailableVenues(Array.from(venueMap.entries()).map(([id, name]) => ({ id, name })));
      
      setShowForm(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load venue group details');
    }
  };

  const loadVenueNamesForIds = async (venueIds: string[]) => {
    if (!token || venueIds.length === 0) return;
    try {
      const response = await apiRequest<{ venues: Array<{ id: string; name: string }> }>(
        `/admin/venues?region=${selectedRegion}&limit=100`,
        token
      );
      const venueMap = new Map<string, string>();
      (response.venues || []).forEach(v => {
        if (venueIds.includes(v.id)) {
          venueMap.set(v.id, v.name);
        }
      });
      setAvailableVenues(Array.from(venueMap.entries()).map(([id, name]) => ({ id, name })));
    } catch (err: any) {
      console.error('Failed to load venue names:', err);
    }
  };

  const loadMapMarkers = async () => {
    if (!token || !selectedRegion) return;
    try {
      setLoadingMapMarkers(true);
      const grouped = await fetchMapMarkerCdnOptions(token, selectedRegion);
      setMapMarkerGroups(grouped);
    } catch (err: any) {
      console.error('Failed to load map markers:', err);
      setMapMarkerGroups({ shared: [], regional: [] });
    } finally {
      setLoadingMapMarkers(false);
    }
  };

  const loadRegions = async () => {
    if (!token) return;
    try {
      const response = await apiRequest<{ regions: Array<{ region_name: string; metro_name: string }> | null; count: number }>(
        '/admin/regions',
        token
      );
      const sorted = sortRegions(response.regions || []);
      setRegions(sorted);
      if (sorted.length > 0 && !selectedRegion) {
        setSelectedRegion(sorted[0].region_name);
      }
    } catch (err: any) {
      console.error('Failed to load regions:', err);
    }
  };

  const loadVenueGroups = async () => {
    if (!token || !selectedRegion) return;
    try {
      setLoading(true);
      const response = await apiRequest<{ venue_groups: VenueGroupingWithVenues[] }>(
        `/admin/venues/groups?region=${selectedRegion}`,
        token
      );
      setVenueGroups(response.venue_groups || []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load venue groups');
      setVenueGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const searchVenues = async (search: string) => {
    if (!token || !search || search.length < 2) {
      setAvailableVenues([]);
      return;
    }
    try {
      const response = await apiRequest<{ venues: Array<{ id: string; name: string }> }>(
        `/admin/venues?region=${selectedRegion}&limit=20`,
        token
      );
      const filtered = (response.venues || [])
        .filter(v => v.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 10);
      setAvailableVenues(filtered);
    } catch (err: any) {
      console.error('Failed to search venues:', err);
      setAvailableVenues([]);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (venueSearchTerm) {
        searchVenues(venueSearchTerm);
      } else {
        setAvailableVenues([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueSearchTerm, token, selectedRegion]);

  const handleCreate = () => {
    setEditingGroup(null);
    setFormData({
      region_name: selectedRegion,
      venue_ids: selectedVenueIds.length > 0 ? selectedVenueIds : [],
    });
    setSelectedVenueIds(selectedVenueIds.length > 0 ? selectedVenueIds : []);
    setShowForm(true);
  };

  const handleEdit = async (group: VenueGroup) => {
    if (!token) return;
    try {
      const response = await apiRequest<VenueGroupingDetail>(
        `/admin/venues/groups/${group.id}`,
        token
      );
      setEditingGroup(response.venueGroup);
      setFormData({
        ...response.venueGroup,
        venue_ids: response.venueList.map(v => v.id),
      });
      setSelectedVenueIds(response.venueList.map(v => v.id));
      setShowForm(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load venue group details');
    }
  };

  const handleDelete = async (group: VenueGroup) => {
    if (!token || !window.confirm(`Are you sure you want to delete venue group "${group.name}"? This will remove the grouping but not delete the venues themselves.`)) {
      return;
    }
    try {
      await apiRequest(`/admin/venues/groups/${group.id}`, token, {
        method: 'DELETE',
      });
      await loadVenueGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to delete venue group');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      const payload = {
        name: formData.name,
        latitude: formData.latitude,
        longitude: formData.longitude,
        address: formData.address,
        map_marker: formData.map_marker,
        venue_icon: formData.venue_icon,
        website: formData.website,
        phone: formData.phone,
        region_name: selectedRegion,
        venue_ids: formData.venue_ids || selectedVenueIds,
      };

      if (editingGroup) {
        await apiRequest(`/admin/venues/groups/${editingGroup.id}`, token, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiRequest('/admin/venues/groups', token, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setShowForm(false);
      setSelectedVenueIds([]);
      setVenueSearchTerm('');
      await loadVenueGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to save venue group');
    }
  };

  const handleAddVenue = (venueId: string) => {
    if (!selectedVenueIds.includes(venueId)) {
      const newIds = [...selectedVenueIds, venueId];
      setSelectedVenueIds(newIds);
      setFormData(prev => ({
        ...prev,
        venue_ids: newIds,
      }));
    }
    setVenueSearchTerm('');
    setAvailableVenues([]);
  };

  const handleRemoveVenue = (venueId: string) => {
    const newIds = selectedVenueIds.filter(id => id !== venueId);
    setSelectedVenueIds(newIds);
    setFormData(prev => ({
      ...prev,
      venue_ids: newIds,
    }));
  };

  const filteredGroups = (venueGroups || []).filter(group => {
    if (!group?.venueGroup) return false;
    const needle = searchTerm.trim().toLowerCase();
    if (needle === '') return true;

    const groupName = (group.venueGroup.name || '').toLowerCase();
    const groupId = (group.venueGroup.id || '').toLowerCase();
    const venueIds = (group.venueList || []).map(v => (v.id || '').toLowerCase());

    return (
      groupName.includes(needle) ||
      groupId.includes(needle) ||
      venueIds.some(id => id.includes(needle))
    );
  });

  if (loading && venueGroups.length === 0) {
    return (
      <PageLayout title="Venue Grouping">
        <div>Loading...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Venue Grouping">
      <div className="venue-grouping-page">
        {error && <div className="error-message">{error}</div>}

        <div className="controls">
          <div className="region-selector">
            <label>
              Region:
              <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
                {regions.map(region => (
                  <option key={region.region_name} value={region.region_name}>
                    {region.metro_name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name, group id, or venue id..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button className="btn btn-primary" onClick={handleCreate}>
            Create Venue Group
          </button>
        </div>

        {showForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <VenueNameWithBranding
                name={formData.name || (editingGroup ? 'Edit Venue Group' : 'Create Venue Group')}
                mapMarker={formData.map_marker}
                venueIcon={formData.venue_icon}
                subtitle={editingGroup ? `Group ${editingGroup.id}` : undefined}
              />
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    Name *:
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </label>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      Latitude *:
                      <input
                        type="number"
                        step="any"
                        value={formData.latitude || ''}
                        onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      Longitude *:
                      <input
                        type="number"
                        step="any"
                        value={formData.longitude || ''}
                        onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Address Line 1:
                    <input
                      type="text"
                      value={formData.address?.line1 || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, line1: e.target.value }
                      })}
                    />
                  </label>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      City:
                      <input
                        type="text"
                        value={formData.address?.city || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          address: { ...formData.address, city: e.target.value }
                        })}
                      />
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      State:
                      <input
                        type="text"
                        value={formData.address?.state || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          address: { ...formData.address, state: e.target.value }
                        })}
                      />
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      ZIP Code:
                      <input
                        type="text"
                        value={formData.address?.postal_code || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          address: { ...formData.address, postal_code: e.target.value }
                        })}
                      />
                    </label>
                  </div>
                </div>

                {loadingMapMarkers ? (
                  <div className="form-group">Loading markers...</div>
                ) : (
                  <>
                    <BrandingSelectPreview label="Map marker (map pins)" value={formData.map_marker}>
                      <select
                        value={formData.map_marker || ''}
                        onChange={(e) => setFormData({ ...formData, map_marker: e.target.value })}
                      >
                        <option value="">None</option>
                        {formData.map_marker &&
                          !flattenMapMarkerOptions(mapMarkerGroups).some(
                            (o) => o.value === formData.map_marker
                          ) && (
                            <option value={formData.map_marker}>
                              {formData.map_marker} (current, not in list)
                            </option>
                          )}
                        <optgroup label="CDN root (shared — includes default icon)">
                          {mapMarkerGroups.shared.map((opt) => (
                            <option key={`mm-${opt.value}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                        {mapMarkerGroups.regional.length > 0 && (
                          <optgroup label={`Regional (${selectedRegion})`}>
                            {mapMarkerGroups.regional.map((opt) => (
                              <option key={`mm-r-${opt.value}`} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </BrandingSelectPreview>

                    <BrandingSelectPreview label="Venue icon (list & calendar)" value={formData.venue_icon}>
                      <select
                        value={formData.venue_icon || ''}
                        onChange={(e) => setFormData({ ...formData, venue_icon: e.target.value })}
                      >
                        <option value="">None (falls back to map marker)</option>
                        {formData.venue_icon &&
                          !flattenMapMarkerOptions(mapMarkerGroups).some(
                            (o) => o.value === formData.venue_icon
                          ) && (
                            <option value={formData.venue_icon}>
                              {formData.venue_icon} (current, not in list)
                            </option>
                          )}
                        <optgroup label="CDN root (shared — includes default icon)">
                          {mapMarkerGroups.shared.map((opt) => (
                            <option key={`vi-${opt.value}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                        {mapMarkerGroups.regional.length > 0 && (
                          <optgroup label={`Regional (${selectedRegion})`}>
                            {mapMarkerGroups.regional.map((opt) => (
                              <option key={`vi-r-${opt.value}`} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </BrandingSelectPreview>
                  </>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      Website:
                      <input
                        type="url"
                        value={formData.website || ''}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      Phone:
                      <input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Venues *:
                    <div className="venue-selector">
                      <input
                        type="text"
                        placeholder="Search venues to add..."
                        value={venueSearchTerm}
                        onChange={(e) => setVenueSearchTerm(e.target.value)}
                      />
                      {availableVenues.length > 0 && (
                        <div className="venue-search-results">
                          {availableVenues
                            .filter(v => !selectedVenueIds.includes(v.id))
                            .map(venue => (
                              <div
                                key={venue.id}
                                className="venue-search-result-item"
                                onClick={() => handleAddVenue(venue.id)}
                              >
                                {venue.name} ({venue.id})
                              </div>
                            ))}
                        </div>
                      )}
                      <div className="selected-venues">
                        {selectedVenueIds.map(venueId => {
                          const venue = availableVenues.find(v => v.id === venueId) || 
                            venueGroups.flatMap(g => g.venueList || []).find(v => v.id === venueId);
                          return (
                            <div key={venueId} className="selected-venue-tag">
                              {venue ? `${venue.name} (${venue.id})` : venueId}
                              <button
                                type="button"
                                onClick={() => handleRemoveVenue(venueId)}
                                className="remove-venue-btn"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingGroup ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowForm(false);
                      setSelectedVenueIds([]);
                      setVenueSearchTerm('');
                      setFormData({});
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="venue-groups-list">
          {filteredGroups.length === 0 ? (
            <div className="no-results">No venue groups found</div>
          ) : (
            filteredGroups.map(group => (
              <div key={group.venueGroup.id} className="venue-group-card">
                <div className="venue-group-header">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', margin: 0 }}>
                    <VenueBrandingPair
                      mapMarker={group.venueGroup.map_marker}
                      venueIcon={group.venueGroup.venue_icon}
                    />
                    <span>
                      {group.venueGroup.name}{' '}
                      <span style={{ fontWeight: 400, opacity: 0.75 }}>
                        ({group.venueGroup.id})
                      </span>
                    </span>
                  </h4>
                  <div className="venue-group-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(group.venueGroup)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(group.venueGroup)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="venue-group-details">
                  <div className="detail-item">
                    <strong>Region:</strong> {group.venueGroup.region_name}
                  </div>
                  <div className="detail-item">
                    <strong>Coordinates:</strong> {group.venueGroup.latitude}, {group.venueGroup.longitude}
                  </div>
                  {(group.venueGroup.map_marker || group.venueGroup.venue_icon) && (
                    <div className="detail-item venue-group-card-branding">
                      {group.venueGroup.map_marker && (
                        <span className="venue-group-card-branding__label">
                          <VenueBrandingThumb storedValue={group.venueGroup.map_marker} alt="Map pin" size="sm" />
                          {group.venueGroup.map_marker}
                        </span>
                      )}
                      {group.venueGroup.venue_icon && (
                        <span className="venue-group-card-branding__label">
                          <VenueBrandingThumb
                            storedValue={group.venueGroup.venue_icon}
                            alt="List/calendar icon"
                            size="sm"
                          />
                          {group.venueGroup.venue_icon}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="detail-item">
                    <strong>Venues ({(group.venueList || []).length}):</strong>
                    <div className="venue-list">
                      {(group.venueList || []).map(venue => (
                        <span key={venue.id} className="venue-tag">
                          {venue.name} ({venue.id})
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default VenueGroupingPage;
